import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  ModuleRegistry,
  TextFilterModule,
  ValidationModule,
} from "ag-grid-community";
import {
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  FiltersToolPanelModule,
  PivotModule,
  RowGroupingPanelModule,
} from "ag-grid-enterprise";
import { getAgGridTheme } from "../common/AgGridTheme";
import Pagination from "./CustomPagination";
import {
  useGetListForProviderTherapeutic,
  useGetTAIndicationsData,
  useGTASummaryData,
} from "../custom-hooks/queries";
import PageLoading from "../pages/SponsorUser/PageLoading";
import { TASummary } from "./TASummary";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
  ContextMenuModule,
  PivotModule,
  FiltersToolPanelModule,
  RowGroupingPanelModule,
  TextFilterModule,
  ValidationModule, // Development Only
]);

const defaultPagination = {
  page: 1,
  size: 30,
  total: 100,
};

const valueColumns = [
  {
    field: "Number of Trials in Indication",
    headerName: "Trials in Indication",
    aggFunc: "sum",
    headerValueGetter: (params) => params.colDef.headerName,
  },
  {
    field: "Number of Phase 1 Trials",
    headerName: "Phase 1 Trials",
    aggFunc: "sum",
    headerValueGetter: (params) => params.colDef.headerName,
  },
  {
    field: "Number of Phase 1 Healthy Volunteers",
    headerName: "Phase 1 Healthy Volunteers",
    aggFunc: "sum",
    headerValueGetter: (params) => params.colDef.headerName,
  },
  {
    field: "Number of Phase 1 Patients",
    headerName: "Phase 1 Patients",
    aggFunc: "sum",
    headerValueGetter: (params) => params.colDef.headerName,
  },
  {
    field: "Number of Phase 2 Trials",
    headerName: "Phase 2 Trials",
    aggFunc: "sum",
    headerValueGetter: (params) => params.colDef.headerName,
  },
  {
    field: "Number of Phase 2 Patients",
    headerName: "Phase 2 Patients",
    aggFunc: "sum",
    headerValueGetter: (params) => params.colDef.headerName,
  },
  {
    field: "Number of Phase 3 Trials",
    headerName: "Phase 3 Trials",
    aggFunc: "sum",
    headerValueGetter: (params) => params.colDef.headerName,
  },
  {
    field: "Number of Phase 3 Patients",
    headerName: "Phase 3 Patients",
    aggFunc: "sum",
    headerValueGetter: (params) => params.colDef.headerName,
  },
];

const getColumnDefs = (pivotMode, originalTAOrder) => {
  const taComparator = (a, b) => {
    const idxA = originalTAOrder?.indexOf(a);
    const idxB = originalTAOrder?.indexOf(b);
    if (idxA === -1 && idxB === -1) return a?.localeCompare(b);
    if (idxA === -1) return 1;
    if (idxB === -1) return -1;
    return idxA - idxB;
  };

  // Default: No columns are pivoted, but all three are row groups
  return [
    { field: "CRO", headerName: "Vendor", rowGroup: true },
    { field: "Therapeutic Area", rowGroup: true, comparator: taComparator },
    { field: "Indication", rowGroup: true },
    ...valueColumns,
  ];
};

const TAIndicationChart = ({
  selectedVendors = [],
  selectedCategories = [],
  selectedVendorType = [],
  selectedTAIndications = {},
  selectedDocAvailability = [],
  selectedEmployeeSizes = [],
  selectedDataAvailability = [],
  selectedRevenueRanges = [],
  selectedLocationFilters = [],
}) => {
  // Get the original TA order from the full data set
  const [pivotMode, setPivotMode] = useState(true);
  const [selectedTA, setSelectedTA] = useState("All");
  const [taSelectorOpen, setTaSelectorOpen] = useState(false);
  const taSelectorRef = useRef(null);
  const gridRef = useRef();
  const myCustomTheme = useMemo(() => getAgGridTheme(), []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        taSelectorRef.current &&
        !taSelectorRef.current.contains(event.target)
      ) {
        setTaSelectorOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 130,
      enableValue: true,
      enableRowGroup: true,
      enablePivot: true,
      resizable: true,
      sortable: true,
      filter: true,
    }),
    []
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      minWidth: 200,
      pinned: "left",
    }),
    []
  );

  const [summaryFilters, setSummaryFilters] = useState({
    keyMetric: "Trials",
    selectedTA: "All",
  });
  const { data: therapeutics = [] } = useGetListForProviderTherapeutic();
  const [pagination, setPagination] = useState(defaultPagination);

  const { data: TAIndicationsData, isLoading: gridLoading } =
    useGetTAIndicationsData(pagination, {
      selectedVendors,
      selectedVendorType,
      selectedCategories,
      selectedTAIndications,
      selectedEmployeeSizes,
      selectedDocAvailability,
      selectedDataAvailability,
      selectedRevenueRanges,
      selectedLocationFilters,
    });

  const { data: TASummaryData, isLoading: summaryLoading } = useGTASummaryData(
    defaultPagination,
    {
      selectedVendors,
      selectedVendorType,
      selectedCategories,
      selectedTAIndications,
      selectedEmployeeSizes,
      selectedDocAvailability,
      selectedDataAvailability,
      selectedRevenueRanges,
      selectedLocationFilters,
      summaryFilters: {
        ...summaryFilters,
        selectedTA:
          summaryFilters.selectedTA !== "All"
            ? therapeutics?.find(
                (ta) => ta.therapeutic_area === summaryFilters.selectedTA
              )?.therapeutic_area_id
            : "All",
      },
    }
  );

  const TAIndications = useMemo(
    () => TAIndicationsData?.data || [],
    [TAIndicationsData]
  );
  const paginationData = useMemo(
    () => TAIndicationsData?.pagination || defaultPagination,
    [TAIndicationsData]
  );
  const TASummaryList = useMemo(
    () => TASummaryData?.data || {},
    [TASummaryData]
  );

  const originalTAOrder = useMemo(() => {
    const allData = [...TAIndications];
    const seen = new Set();
    return allData
      .map((row) => row["Therapeutic Area"])
      .filter((ta) => {
        if (seen.has(ta)) return false;
        seen.add(ta);
        return true;
      });
  }, [TAIndications]);

  const [columnDefs, setColumnDefs] = useState(
    getColumnDefs(true, originalTAOrder)
  );

  const filteredData = useMemo(() => {
    const data = [...TAIndications];
    // Sort by original TA order, then Indication
    return data.slice().sort((a, b) => {
      const keyA = { vendor: a["CRO"], ta: a["Therapeutic Area"] };
      const keyB = { vendor: b["CRO"], ta: b["Therapeutic Area"] };
      const idxA = originalTAOrder.findIndex((x) => x === keyA.ta);
      const idxB = originalTAOrder.findIndex((x) => x === keyB.ta);
      if (idxA !== idxB) return idxA - idxB;
      if (a["Indication"] < b["Indication"]) return -1;
      if (a["Indication"] > b["Indication"]) return 1;
      return 0;
    });
  }, [TAIndications, originalTAOrder]);

  // Get available therapeutic areas for dropdown
  const availableTherapeuticAreas = useMemo(() => {
    const areas = new Set();
    filteredData.forEach((item) => areas.add(item["Therapeutic Area"]));
    return ["All", ...Array.from(areas).sort()];
  }, [filteredData]);

  const onChangeSummaryFilters = (filters) => {
    setSummaryFilters(filters);
  };

  const onColumnPivotModeChanged = useCallback(() => {
    if (gridRef.current) {
      const api = gridRef.current.api;
      const isPivot = api.isPivotMode();
      setPivotMode(isPivot);
      setColumnDefs(getColumnDefs(isPivot, originalTAOrder));
    }
  }, [originalTAOrder]);

  const onPageChange = (page) => {
    setPagination({ ...pagination, page });
  };

  const onGridReady = useCallback((params) => {
    setPivotMode(params.api.isPivotMode());
    setColumnDefs(getColumnDefs(params.api.isPivotMode(), originalTAOrder));
  }, []);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      {/* Single Frame Container for Dashboard and Grid */}
      <div className="mt-6 mb-4 bg-white rounded-lg border border-gray-200 shadow-sm">
        {summaryLoading ? (
          <PageLoading label="Loading Summary" />
        ) : (
          <TASummary
            vendorSummaryData={TASummaryList}
            availableTherapeuticAreas={availableTherapeuticAreas}
            summaryFilters={summaryFilters}
            onChangeSummaryFilters={onChangeSummaryFilters}
          />
        )}

        {/* Grid Section */}
        {gridLoading ? (
          <PageLoading label="Loading Risk Data" />
        ) : (
          <div className="px-4 pb-4">
            <div
              className="mt-4 rounded-lg border border-gray-200"
              style={{
                height: 1000,
                width: "100%",
                backgroundColor: "#ffffff", // Theme background color
                borderRadius: "15px", // Theme border radius
                overflow: "hidden",
              }}
            >
              <AgGridReact
                ref={gridRef}
                rowData={filteredData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                autoGroupColumnDef={autoGroupColumnDef}
                pivotMode={pivotMode}
                loading={gridLoading}
                sideBar={{
                  toolPanels: [
                    {
                      id: "columns",
                      labelDefault: "Columns",
                      iconKey: "columns",
                      toolPanel: "agColumnsToolPanel",
                    },
                    {
                      id: "filters",
                      labelDefault: "Filters",
                      iconKey: "filter",
                      toolPanel: "agFiltersToolPanel",
                    },
                  ],
                  defaultToolPanel: "filters",
                }}
                pivotPanelShow={"always"}
                onColumnPivotModeChanged={onColumnPivotModeChanged}
                onGridReady={onGridReady}
                suppressGroupChangesColumnVisibility="suppressHideOnGroup"
                suppressAggFuncInHeader={true}
                headerHeight={38}
                groupHeaderHeight={28}
                pivotHeaderHeight={38}
                pivotGroupHeaderHeight={28}
                rowHeight={48}
                theme={myCustomTheme}
                domLayout="normal"
              />
            </div>
            <div className="mt-5">
              <Pagination
                totalPages={paginationData.total}
                currentPage={paginationData.page}
                onPageChange={onPageChange}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TAIndicationChart;
